import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ApiError } from '@/types/api'

// Use proxy in development, Cloudflare Worker proxy in production
const getApiBaseUrl = () => {
  if (import.meta.env.DEV) {
    // Use Vite proxy in development
    return '/api-proxy'
  }
  
  // In production, always use Cloudflare Worker proxy
  // Worker URL from environment variable or default
  const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://api-proxy.abofakherlaila.workers.dev'
  
  // Log for debugging (will be removed in production build)
  console.log('API Base URL:', workerUrl)
  
  return workerUrl
}

const API_BASE_URL = getApiBaseUrl()

const DROPSHIPPING_CLIENT_ID = import.meta.env.VITE_DROPSHIPPING_CLIENT_ID || '71'
const API_VERSION = '2.1.0'

// Log API configuration (only in development)
if (import.meta.env.DEV) {
  console.log('API Configuration:', {
    baseURL: API_BASE_URL,
    dropshippingClientId: DROPSHIPPING_CLIENT_ID,
    apiVersion: API_VERSION,
    fullURL: `${API_BASE_URL}/dropshipping-api/${DROPSHIPPING_CLIENT_ID}/${API_VERSION}`,
  })
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/dropshipping-api/${DROPSHIPPING_CLIENT_ID}/${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add authentication
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Only add auth in development (when using Vite proxy)
    // In production, Worker handles authentication
    if (import.meta.env.DEV) {
      const username = import.meta.env.VITE_API_USERNAME
      const password = import.meta.env.VITE_API_PASSWORD

      if (username && password) {
        // UTF-8 safe Base64 encoding for Basic Authentication
        const encodeBase64 = (str: string) => {
          try {
            return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_match, p1) => {
              return String.fromCharCode(parseInt(p1, 16));
            }));
          } catch (e) {
            console.error('Base64 encoding failed:', e);
            return btoa(str);
          }
        };
        config.headers.Authorization = `Basic ${encodeBase64(`${username}:${password}`)}`;
      }
    }
    // In production, Worker adds authentication, so we don't add it here

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // Handle CORS/Network errors (they don't have a response)
    if (!error.response && error.request) {
      const errorMessage = (error as any).message || ''
      const isCorsError = 
        error.code === 'ERR_NETWORK' || 
        errorMessage.includes('CORS') ||
        errorMessage.includes('blocked by CORS') ||
        errorMessage.includes('preflight')
      
      if (isCorsError) {
        console.error('CORS Error: Server does not allow requests from this origin')
        return Promise.reject({
          error: 'CORS_ERROR',
          traceId: undefined,
        } as ApiError)
      }
      
      console.error('Network Error:', errorMessage)
      return Promise.reject({
        error: 'NETWORK_ERROR',
        traceId: undefined,
      } as ApiError)
    }

    // Handle API response errors
    if (error.response) {
      const { data, status, headers } = error.response
      const traceId = headers['x-trace-id'] || (data as any)?.traceId
      const responseData = data as any

      // Log error with trace ID and full response for debugging
      console.error('API Error:', {
        status,
        statusText: error.response.statusText,
        error: responseData?.error || (error as any).message,
        message: responseData?.message,
        traceId,
        responseData: responseData, // Log full response to see validation details
        requestUrl: error.config?.url,
        requestMethod: error.config?.method,
        requestData: error.config?.data, // Log request data
        baseURL: error.config?.baseURL,
        fullResponse: JSON.stringify(responseData, null, 2), // Full JSON response
      })

      // For 503 errors, provide more context
      if (status === 503) {
        console.error('503 Service Unavailable - Possible causes:', {
          workerUrl: API_BASE_URL,
          isDev: import.meta.env.DEV,
          isProduction: !import.meta.env.DEV,
          suggestion: 'Check if Worker is deployed and running. Check Worker logs for details.',
        })
      }

      // Return formatted error with response status
      let errorMessage = responseData?.error || (error as any).message || 'An unexpected error occurred';

      if (status === 403) {
        errorMessage = 'Zugriff verweigert (403): Ihr API-Benutzer hat keine Berechtigung für diesen Vorgang oder diesen Dropshipping-Client. Bitte prüfen Sie die Berechtigungen im Portal.';
      } else if (status === 400) {
        const details = responseData?.error || responseData?.message || 'Validierungsfehler';
        errorMessage = `Ungültige Anfrage (400): ${details}. Bitte prüfen Sie Ihre Eingaben.`;
      }

      const apiError: ApiError = {
        error: errorMessage,
        traceId,
      }
      
      // Store full details in the error object (not in ApiError type, but accessible)
      ;(apiError as any).details = responseData
      ;(apiError as any).status = status
      ;(apiError as any).statusText = error.response.statusText
      ;(apiError as any).response = error.response

      return Promise.reject(apiError)
    }

    // Fallback for unknown errors
    return Promise.reject({
      error: (error as any).message || 'An unexpected error occurred',
    } as ApiError)
  }
)

export default apiClient
